import axios, {
    AxiosRequestConfig, AxiosResponse,
} from 'axios';
import { t } from 'i18next';
import { config } from '../constants/config';
import UserTokenService from './user-token';

type TFetchServiceHeaders = Record<string, string>;

interface IFetchServiceResponse<T> {
    success: boolean,
    message: string,
    data: T|null,
    status: number;
    errors?: Record<string, string[]>;
}

export type TFetchServiceRequestConfig = AxiosRequestConfig & {
    headers?: TFetchServiceHeaders;
};

axios.interceptors.response.use(undefined, (err: {response: AxiosResponse}) => {
    if ([401, 403, 404, 422].includes(err.response.status)) {
        return err.response;
    }
    return Promise.reject(err);
});

class FetchService {
    /**
     * Скачать файл
     * @param reqConfig - параметры запроса
     * @returns Promise<IFetchServiceResponse<T>>
     */
    public static downloadFile = async (reqConfig: TFetchServiceRequestConfig):Promise<IFetchServiceResponse<Blob|null>> => {
        try {
            const { data, status } = await axios({
                baseURL: config.apiUrl,
                ...reqConfig,
                headers: FetchService.getHeaders(reqConfig.headers),
                responseType: 'blob',
            });
            if (status === 200) {
                return {
                    success: true,
                    data,
                    status,
                    message: t('fileDownloadedSuccessfully'),
                };
            }
            return {
                success: false,
                data: null,
                message: t('fileDownloadError'),
                status: 500,
            };
        } catch (err) {
            return {
                success: false,
                data: null,
                message: t('fileDownloadError'),
                status: 500,
            };
        }
    };

    /**
     * Получить заголовки HTTP-запроса
     * @param predefined - заголовки запроса { key: value }
     * @returns TFetchServiceHeaders
     */
    public static getHeaders = (predefined?: TFetchServiceHeaders): TFetchServiceHeaders => {
        const headers: TFetchServiceHeaders = {
            'Content-Type': 'application/json',
            ...predefined,
        };

        const userToken = UserTokenService.getToken();
        if (typeof userToken === 'string') {
            headers.Authorization = `Bearer ${userToken}`;
        }

        return headers;
    };

    /**
     * Создать запрос на сервер
     * @param reqConfig - параметры запроса
     * @returns Promise<IFetchServiceResponse<T>>
     */
    public static makeRequest = async <T = any>(reqConfig: TFetchServiceRequestConfig): Promise<IFetchServiceResponse<T>> => {
        try {
            const { data, status } = await axios({
                baseURL: config.apiUrl,
                ...reqConfig,
                headers: FetchService.getHeaders(reqConfig.headers),
                withCredentials: true,
            });
            return {
                ...data,
                status,
            };
        } catch (err) {
            return {
                success: false,
                data: null,
                message: t('httpRequestServeError'),
                status: 500,
            };
        }
    };
}

export default FetchService;
